import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { filter, map, combineLatest, of, startWith, take, timeout } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../services/auth.service';
import { IconComponent } from '../ui/icon.component';
import { ButtonDirective } from '../ui/button.directive';
import { ToastOutletComponent } from '../ui/overlay/toast-outlet.component';
import { ConfirmOutletComponent } from '../ui/overlay/confirm-outlet.component';
import {
  NavItem,
  isAdminItem,
  isAuthItem,
  isMainItem,
  visibleNav,
} from './nav.model';
import { environment } from '../../environments/environment';

/**
 * The whole app chrome: topbar, desktop nav, mobile drawer, profile menu and the two
 * overlay outlets. Replaces the seven Poseidon layout components, whose sidebar modes,
 * config panel and theme switcher were never reachable.
 *
 * `.layout-container` stays on the outer div: scripts/screenshots.mjs and
 * tests/visual/run.mjs wait on it as the "Angular has bootstrapped" signal, and
 * networkidle is useless here because the Firestore WebChannel never idles.
 */
@Component({
  selector: 'app-shell',
  // Eager, not OnPush: every page renders inside this component's <router-outlet>, and
  // the routed screens still hold plain fields rather than signals. An OnPush ancestor
  // that is not dirty skips its whole subtree, so those pages would never re-render --
  // only the ones driven by | async, which calls markForCheck itself. Revisit once the
  // screens are signal-based.
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    TitleCasePipe,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    IconComponent,
    ButtonDirective,
    ToastOutletComponent,
    ConfirmOutletComponent,
  ],
  templateUrl: './shell.component.html',
})
export class ShellComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly menuOpen = signal(false);

  /**
   * Which of the three not-the-real-site environments this is, or null on production.
   * Read from the hostname rather than a build flag: test and demo ship the same
   * artifact and differ only in which commit reached them.
   */
  private readonly environmentKind = ((): 'demo' | 'test' | 'local' | null => {
    if (environment.production) {
      return null;
    }
    const host = typeof location === 'undefined' ? '' : location.hostname;
    if (host.startsWith('demo.') || host.startsWith('reserve-it-demo')) {
      return 'demo';
    }
    if (host.startsWith('test.') || host.startsWith('reserve-it-test')) {
      return 'test';
    }
    return 'local';
  })();

  /**
   * Named on every screen that is not the real site, because all three look identical
   * and two of them accept bookings that mean nothing. A member who lands on test from
   * a bookmark, or a visitor clicking through the demo, has no other way to tell.
   */
  protected readonly environmentLabel: string | null = this.environmentKind
    ? {
        demo: 'Demo omgeving',
        test: 'Test omgeving',
        local: 'Lokaal',
      }[this.environmentKind]
    : null;

  /**
   * The demo exists to be shown to people, so it is the one place that asks the visitor
   * a question. Not on test, which the club uses, and not on production, which is theirs
   * rather than a shop window: both carry only the footer credit below. It renders
   * locally too, so the UI loop and scripts/ui-audit.mjs see the same screen a visitor
   * does.
   */
  protected readonly showCallout =
    this.environmentKind === 'demo' || this.environmentKind === 'local';

  /**
   * Where a lead came from, readable in any analytics tool and in a plain access log.
   * The medium tells the callout and the footer credit apart; the credit is on every
   * environment, production included, so its clicks say something different.
   */
  protected readonly portfolioUrl =
    'https://wvince.dev/?utm_source=spinning-westerlo&utm_medium=demo';
  protected readonly creditUrl =
    'https://wvince.dev/?utm_source=spinning-westerlo&utm_medium=footer';

  /** Dismissal lasts the browsing session: a returning visitor is a new conversation. */
  protected readonly calloutDismissed = signal(
    typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem('callout-dismissed') === '1',
  );

  protected dismissCallout(): void {
    this.calloutDismissed.set(true);
    sessionStorage.setItem('callout-dismissed', '1');
  }
  protected readonly user$ = this.auth.user$;

  /** Empty until the navigation facts are settled, so the bar cannot render a guest's
   *  four entries and swap to a member's five or an admin's nine a moment later. The
   *  header keeps its height either way, so the links appear rather than shift.
   *
   *  The 5s fallback matches the guards: if auth never resolves the app is broken, and
   *  a menu that never appears would hide the way out of it. */
  private readonly navReady = this.auth.navReady$.pipe(
    filter(Boolean),
    take(1),
    timeout({ first: 5000, with: () => of(true) }),
    startWith(false),
  );

  protected readonly nav = toSignal(
    combineLatest([
      this.navReady,
      this.auth.userEmailVerified$,
      this.auth.userIsAdmin$,
    ]).pipe(
      map(([ready, member, admin]) => (ready ? visibleNav(member, admin) : [])),
    ),
    { initialValue: [] as NavItem[] },
  );

  /** The desktop bar's three slots. The drawer still renders `nav()` whole. */
  protected readonly mainNav = computed(() => this.nav().filter(isMainItem));
  protected readonly adminNav = computed(() => this.nav().filter(isAdminItem));
  protected readonly authNav = computed(() => this.nav().filter(isAuthItem));

  /** Closes the admin menu; a <details> left open would hang over the next page. */
  protected readonly adminMenu =
    viewChild<ElementRef<HTMLDetailsElement>>('adminMenu');

  constructor() {
    // Close the drawer on navigation, otherwise it stays over the page it just opened.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.menuOpen.set(false);
        const menu = this.adminMenu()?.nativeElement;
        if (menu) {
          menu.open = false;
        }
      });
  }

  /** Up to two letters: "Demo Gebruiker" -> DG, a single name -> its first letter. */
  protected initials(user: {
    displayName?: string;
    firstName?: string;
  }): string {
    const name = (user.displayName || user.firstName || '').trim();
    if (!name) {
      return '?';
    }
    const parts = name.split(/\s+/);
    const letters =
      parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0][0];
    return letters.toUpperCase();
  }

  protected toggleMenu() {
    this.menuOpen.update((open) => !open);
  }

  protected async logout() {
    this.menuOpen.set(false);
    await this.auth.logoutUser();
  }
}
